import { useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronDown, Mail, MessageCircle, Phone } from 'lucide-react';
import { Skeleton } from '@tcrd/ui';
import { ServiceIcon } from '../../components/ServiceIcon';
import { env } from '../../lib/env';
import { useAuth } from '../../stores/auth';
import { MarkBadge } from './LandingPage';
import { usePublicProfile, whatsappLink } from './usePublicProfile';

const TITLES: Record<string, string> = {
  ayuda: 'Centro de ayuda',
  nosotros: 'Quiénes somos',
  servicios: 'Lo que ofrecemos',
  socio: 'Socio conductor',
};

/** Páginas del pie de la portada, con el mismo estilo minimalista. El contenido viene de la configuración del negocio. */
export function InfoPage() {
  const { doc } = useParams<{ doc: string }>();
  const { data, isLoading } = usePublicProfile();
  const status = useAuth((s) => s.status);
  const [open, setOpen] = useState<number | null>(0);
  const title = TITLES[doc] ?? TITLES.ayuda!;
  const name = data?.tenant.name ?? 'TuConductorRD';
  const wa = whatsappLink(data?.tenant.whatsapp ?? env.whatsappNumber);
  const waDriver = whatsappLink(data?.tenant.whatsapp ?? env.whatsappNumber, 'Hola, quiero ser socio conductor de TuConductorRD.');

  const Contact = () => (
    <div className="flex flex-col gap-3">
      {wa && (
        <a href={wa} target="_blank" rel="noreferrer" className="tap flex items-center gap-3 py-4 border-b border-black/10 dark:border-white/15">
          <MessageCircle className="size-5" aria-hidden /> <span className="flex-1">WhatsApp</span> <ArrowRight className="size-5" aria-hidden />
        </a>
      )}
      {data?.tenant.phone && (
        <a href={`tel:${data.tenant.phone}`} className="tap flex items-center gap-3 py-4 border-b border-black/10 dark:border-white/15">
          <Phone className="size-5" aria-hidden /> <span className="flex-1">{data.tenant.phone}</span> <ArrowRight className="size-5" aria-hidden />
        </a>
      )}
      {data?.tenant.email && (
        <a href={`mailto:${data.tenant.email}`} className="tap flex items-center gap-3 py-4 border-b border-black/10 dark:border-white/15">
          <Mail className="size-5" aria-hidden /> <span className="flex-1 truncate">{data.tenant.email}</span> <ArrowRight className="size-5" aria-hidden />
        </a>
      )}
    </div>
  );

  return (
    <IonPage>
      <IonContent>
        <div className="min-h-full bg-white text-black dark:bg-black dark:text-white">
          <header className="sticky top-0 z-20 bg-white/95 dark:bg-black/95 backdrop-blur pt-[env(safe-area-inset-top)]">
            <div className="mx-auto max-w-3xl px-5 h-16 flex items-center gap-3">
              <Link to="/" className="tap size-9 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center" aria-label="Volver">
                <ArrowLeft className="size-5" />
              </Link>
              <Link to="/" className="flex items-center gap-2.5"><MarkBadge size={32} /> <span className="font-semibold">{name}</span></Link>
            </div>
          </header>
          <main className="mx-auto max-w-3xl px-5 pt-8 pb-16">
            <h1 className="text-4xl font-bold tracking-tight">{title}</h1>
            {isLoading && <Skeleton className="h-40 mt-8 bg-black/5 dark:bg-white/10" />}

            {doc === 'ayuda' && data && (
              <>
                <section className="mt-8">
                  <h2 className="text-lg font-bold mb-2">Preguntas frecuentes</h2>
                  {data.landing.faqs.map((f, i) => (
                    <div key={i} className="border-b border-black/10 dark:border-white/15">
                      <button className="tap w-full py-5 flex items-center justify-between gap-4 text-left font-semibold" onClick={() => setOpen(open === i ? null : i)} aria-expanded={open === i}>
                        {f.q} <ChevronDown className={`size-5 shrink-0 transition-transform ${open === i ? 'rotate-180' : ''}`} aria-hidden />
                      </button>
                      {open === i && <p className="pb-5 text-black/80 dark:text-white/75 leading-relaxed">{f.a}</p>}
                    </div>
                  ))}
                  {data.cancellationPolicy && (
                    <div className="border-b border-black/10 dark:border-white/15 py-5">
                      <p className="font-semibold">Política de cancelación</p>
                      <p className="mt-2 text-black/80 dark:text-white/75 leading-relaxed whitespace-pre-line">{data.cancellationPolicy}</p>
                    </div>
                  )}
                </section>
                <section className="mt-10">
                  <h2 className="text-lg font-bold mb-2">¿Necesitas ayuda? Contáctanos</h2>
                  <Contact />
                </section>
              </>
            )}

            {doc === 'nosotros' && data && (
              <>
                <p className="mt-6 text-lg text-black/85 dark:text-white/80 leading-relaxed">{data.landing.heroSubtitle}</p>
                <section className="mt-8 flex flex-col">
                  {data.landing.benefits.map((b) => (
                    <div key={b.title} className="py-5 border-b border-black/10 dark:border-white/15">
                      <p className="text-xl font-bold">{b.title}</p>
                      <p className="mt-1 text-black/75 dark:text-white/70">{b.text}</p>
                    </div>
                  ))}
                </section>
                {data.coverageText && <p className="mt-8 text-black/75 dark:text-white/70"><strong className="text-white">Cobertura:</strong> {data.coverageText}</p>}
              </>
            )}

            {doc === 'servicios' && data && (
              <section className="mt-8 flex flex-col">
                {data.services.map((s) => (
                  <Link key={s.id} to={status === 'authenticated' ? `/app/request?service=${s.code}` : '/register?next=/app/request'} className="tap py-5 border-b border-black/10 dark:border-white/15 flex items-center gap-4">
                    <span className="size-12 rounded-2xl bg-black/5 dark:bg-white/10 flex items-center justify-center shrink-0"><ServiceIcon name={s.icon} className="size-6" /></span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-lg font-bold">{s.name}</span>
                      {s.description && <span className="block text-sm text-black/70 dark:text-white/65">{s.description}</span>}
                    </span>
                    <ArrowRight className="size-5 shrink-0" aria-hidden />
                  </Link>
                ))}
              </section>
            )}

            {doc === 'socio' && (
              <>
                <p className="mt-6 text-lg text-black/85 dark:text-white/80 leading-relaxed">
                  Recibe solicitudes de tus propios clientes, cotiza tus precios, compártelo con tu enlace personal y lleva el control de tus ganancias desde la app.
                </p>
                <section className="mt-8 flex flex-col">
                  <Link to="/login" className="tap py-6 border-b border-black/15 dark:border-white/25 flex items-center justify-between">
                    <span><span className="block text-2xl font-bold">Ya soy socio</span><span className="block text-sm text-black/65 dark:text-white/60">Inicia sesión en tu panel</span></span>
                    <ArrowRight className="size-7" aria-hidden />
                  </Link>
                  <Link to="/register?tipo=socio" className="tap py-6 border-b border-black/15 dark:border-white/25 flex items-center justify-between">
                    <span><span className="block text-2xl font-bold">Quiero ser socio</span><span className="block text-sm text-black/65 dark:text-white/60">Regístrate y te aprobamos</span></span>
                    <ArrowRight className="size-7" aria-hidden />
                  </Link>
                  {waDriver && (
                    <a href={waDriver} target="_blank" rel="noreferrer" className="tap py-6 border-b border-black/15 dark:border-white/25 flex items-center justify-between">
                      <span><span className="block text-2xl font-bold">¿Dudas?</span><span className="block text-sm text-black/65 dark:text-white/60">Escríbenos por WhatsApp</span></span>
                      <ArrowRight className="size-7" aria-hidden />
                    </a>
                  )}
                </section>
              </>
            )}
          </main>
        </div>
      </IonContent>
    </IonPage>
  );
}
